<template>
  <v-container>
    <v-btn @click="openModal">Add Cast and Crew</v-btn>
    <v-data-table-server
      v-model:items-per-page="pagination.limit"
      v-model:page="pagination.skip"
      :items="castAndCrewSettings"
      :headers="headers"
      :items-length="count"
      :loading="loading"
      :search="pagination.q"
      item-key="id"
      @update:options="handleLimitChange">
      <template v-slot:top>
        <v-text-field
          v-model="pagination.q"
          class="ma-2"
          density="compact"
          placeholder="Search setting..."
          hide-details></v-text-field>
      </template>
      <template v-slot:[`item.action`]="{ item }">
        <v-btn icon variant="text" small color="primary" @click="openModal(item)">
          <v-icon icon="mdi-pencil"></v-icon>
        </v-btn>
        <v-btn icon variant="text" small color="primary" @click="toggleDelete(item)">
          <v-icon icon="mdi-trash-can-outline"></v-icon>
        </v-btn>
      </template>
    </v-data-table-server>
    <v-dialog v-model="modalOpen" max-width="500px">
      <v-card>
        <v-card-title>{{ selectedCastAndCrewSetting.name }}</v-card-title>
        <v-form ref="form" @submit.prevent="onSubmit">
          <v-card-text>
            <v-text-field
              v-model="selectedCastAndCrewSetting.name"
              label="Name"
              :rules="[rules.required]" />
            <v-text-field
              v-model="selectedCastAndCrewSetting.description"
              label="Description"
              :rules="[rules.required]" />
          </v-card-text>
          <v-card-actions>
            <v-btn color="secondary" @click="canceled">Cancel</v-btn>
            <v-btn color="primary" @click="onSubmit">Save</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
    <ConfirmDeleteComponent
      item="Judge Specialty"
      :is-open="isDeleteOpen"
      :description="selectedCastAndCrewSetting.name || ''"
      @action-canceled="toggleDelete"
      @action-confirmed="removeCast" />
  </v-container>
</template>
<script setup lang="ts">
import { useSettingsStore } from '@/stores/modules/admin';
import { CastCrewDTO, ICastCrewDTO, IPaginationDTO, PaginationDTO } from '@/dtos';
import { computed, ref, watch } from 'vue';
import type { VDataTableServer, VForm } from 'vuetify/components';
import { useUpdateStatusStore } from '@/stores';
import { SNACKSTATUS } from '@/stores/util';
import { rules } from '@/helpers';
import ConfirmDeleteComponent from '@/components/ui/ConfirmDeleteComponent.vue';

const settingsStore = useSettingsStore();
const statusStore = useUpdateStatusStore();
const pagination = ref<IPaginationDTO>(new PaginationDTO());
const { data, isLoading } = settingsStore.getAllCastAndCrewSettings(pagination);
const modalOpen = ref<boolean>(false);
const isDeleteOpen = ref<boolean>(false);
const emit = defineEmits(['snack']);
const castAndCrewSettings = computed(() => data.value?.data ?? []);
const loading = computed(() => isLoading.value);
const count = computed(() => data.value?.count ?? 0);
const form = ref<InstanceType<typeof VForm> | null>();
const selectedCastAndCrewSetting = ref<ICastCrewDTO>(new CastCrewDTO());
const headers = [
  { title: 'Name', key: 'name' },
  { title: 'Description', key: 'description' },
  { title: 'Edit', key: 'action' },
];

const handleLimitChange = (value: VDataTableServer) => {
  pagination.value.buildPagination(value);
};

const canceled = () => {
  selectedCastAndCrewSetting.value = new CastCrewDTO();
  modalOpen.value = false;
};

const onSubmit = async () => {
  if (!selectedCastAndCrewSetting.value.id) {
    await settingsStore.createCastAndCrew.mutateAsync(selectedCastAndCrewSetting.value);
  } else {
    await settingsStore.updateCastAndCrew.mutateAsync(selectedCastAndCrewSetting.value);
  }
};

const openModal = (item: ICastCrewDTO) => {
  selectedCastAndCrewSetting.value = new CastCrewDTO(item);
  modalOpen.value = true;
};
const toggleDelete = (item?: ICastCrewDTO) => {
  selectedCastAndCrewSetting.value = new CastCrewDTO(item);
  isDeleteOpen.value = !isDeleteOpen.value;
};

const removeCast = async () => {
  if (selectedCastAndCrewSetting.value.id) {
    await settingsStore.removeCastAndCrew.mutateAsync(selectedCastAndCrewSetting.value.id);
  }
  toggleDelete();
};

watch(
  () => statusStore.getStatus(`castAndCrew-setting-${selectedCastAndCrewSetting.value.id}`),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    const key = `castAndCrew-setting-${selectedCastAndCrewSetting.value.id}`;
    emit('snack', 'Cast And Crew Setting', value, statusStore.getError(key));
    canceled();
  },
);

watch(
  () => statusStore.getStatus(`castAndCrew-setting-create`),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    const key = `castAndCrew-setting-create`;
    emit('snack', 'Cast And Crew Setting', value, statusStore.getError(key));
    canceled();
  },
);
</script>
